import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useCallback,
} from 'react';
import { Form, Input, Checkbox, Row, Col, Select } from 'antd';
import { isFormIncomplete } from '../../../helpers/formState';
import validator from 'validator';
import { useTranslation } from 'react-i18next';
import {
  useBusinessAddr,
  useCartData,
  useLoading,
  useMbox,
  useServiceablitityData,
  useSessionData,
  useVasData,
} from '../../../hooks/order';
import {
  checkTollFreeNumber,
  getAggregatedAddress,
  getCallLogId,
  getValidAcctEmail,
  handleAlphaKeyPress,
  handleNumericKeyPress,
  isUndefinedNullOrEmpty,
} from '../../../common/common';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/input';
import { useDispatch } from 'react-redux';
import {
  accountCheck,
  saveBusinessInfo,
  updateErrorMsg,
  updateOrder,
  saveSpanishLanguagePreference,
  saveMobileProvider,
  savePhoneNumber
} from '../../../store/action/OrderAction';
import {
  getAccountCheckModel,
  getEnterInfoModel,
} from '../../../helpers/OrderHelperFunctions';
import { alphaWithSpaceRegex, emailRegex, notOnlyNumerics } from '../../../util/regex';
import { Collapse } from 'antd';
import { api } from '../../../util/axios';
import { getConfigs } from '../../../config';
import AddressSuggestionSelect from '../../common/AddressSuggestionSelect';
import { STATES } from '../../../data/states.data';
import spinner from '../../../images/spinner.gif';
import useLeadsSDK from '../../../hooks/useLeadsSDK';
import useRevalidateOnLanguageChange from '../../../hooks/useRevalidateOnLanguageChange';
import PropTypes from 'prop-types';

const config = getConfigs();

const EnterInformation = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const leads = useLeadsSDK();
  const dispatch = useDispatch();
  const { loading } = useLoading();
  const { serviceabilityCheck } = useServiceablitityData();
  const { isVasOnlyFlow } = useVasData();
  const { isBusinessAddress, businessInfo } = useBusinessAddr();
  const { orderInfo } = useSessionData();
  const { normalizedAddress } = useServiceablitityData();
  const { address1, address2, city, state, zipCode } = normalizedAddress;
  const { spanishLanguagePref } = useCartData();
  const {
    orderAccessToken,
    shippingAddress,
    shippingAdd1,
    shippingAdd2,
    shippingCity,
    shippingState,
    shippingZip,
  } = orderInfo;
  const [form] = Form.useForm();
  useRevalidateOnLanguageChange(form);
  const [initialState, setInitialState] = useState({});
  const [emailVal, setEmailVal] = useState('');
  const [emailVerificationError, setEmailVerificationError] = useState('');
  const { internetPlan } = useCartData();
  const { Panel } = Collapse;
  const { isElnkMailboxReq } = useMbox();
  const emailInp = useRef();
  const [open, setOpen] = useState([]);
  const [isEmailAvail, setIsEmailAvail] = useState(true);
  const [isAddressDifferent, setIsAddressDifferent] = useState(false);
  const [isManualAddr, setIsManualAddr] = useState(false);
  const [manualAddress, setManualAddress] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [shippingAddrState, setShippingState] = useState({
    shipAdd1: isUndefinedNullOrEmpty(shippingAdd1)
      ? normalizedAddress.address1
      : shippingAdd1,
    shipAdd2: isUndefinedNullOrEmpty(shippingAdd2)
      ? normalizedAddress.address2
      : shippingAdd2,
    shipCity: isUndefinedNullOrEmpty(shippingCity)
      ? normalizedAddress.city
      : shippingCity,
    shipState: isUndefinedNullOrEmpty(shippingState)
      ? normalizedAddress.state
      : shippingState,
    shipZip: isUndefinedNullOrEmpty(shippingZip)
      ? normalizedAddress.zipCode
      : shippingZip,
    shipAddress: isUndefinedNullOrEmpty(shippingAddress)
      ? getAggregatedAddress(normalizedAddress)
      : shippingAddress,
  });

  const [phoneVerificationError, setPhoneVerificationError] = useState('');
  const [phoneVerificationWarning, setPhoneVerificationWarning] = useState('');
  const pendingSubmitRef = useRef(false);
  const emailNotVerifiedRef = useRef(false);

  const { shipAdd1, shipAdd2, shipCity, shipState, shipZip } =
    shippingAddrState;

  const setShipping = (searchAddress) => {
    setShippingState({
      ...shippingAddrState,
      shipAdd1: searchAddress.address1,
      shipAdd2: searchAddress.address2,
      shipCity: searchAddress.city,
      shipState: searchAddress.state,
      shipZip: searchAddress.zip,
    });

    form.setFieldsValue({
      shippingAddress: getAggregatedAddress({
        address1: searchAddress.address1,
        address2: searchAddress.address2,
        city: searchAddress.city,
        state: searchAddress.state,
        zipCode: searchAddress.zip,
      }),
    });
  };
  const [emailVerificationLoading, setEmailVerificationLoading] =
    useState(false);

  const [phoneVerificationLoading, setPhoneVerificationLoading] = useState(false);

  useEffect(() => {
    const fieldsWithErrors = form
      .getFieldsError()
      .filter((field) => field.errors.length > 0)
      .map((field) => field.name);
    if (fieldsWithErrors.length > 0) {
      form.validateFields(fieldsWithErrors);
    }
  }, [spanishLanguagePref]);

  useEffect(() => {
    if (orderInfo && !isUndefinedNullOrEmpty(orderInfo.firstName)) {
      setInitialState({
        ...orderInfo,
        ...businessInfo,
      });
    }
    const restorableFields = {
      phoneNum: orderInfo.phoneNum ? '+1' + orderInfo.phoneNum : '',
      installPhone: orderInfo.installPhone ? '+1' + orderInfo.installPhone : '',
      firstName: orderInfo.firstName ?? '',
      lastName: orderInfo.lastName ?? '',
      email: orderInfo.email ?? '',
      businessName: businessInfo.businessName ?? '',
      businessPhone: businessInfo.businessPhone ?? '',
      noOfEmployees: businessInfo.noOfEmployees ?? '',
      noOfLocations: businessInfo.noOfLocations ?? '',
    };
    const untouchedFields = Object.fromEntries(
      Object.entries(restorableFields).filter(
        ([field, value]) =>
          !form.isFieldTouched(field) && !isUndefinedNullOrEmpty(value)
      )
    );
    form.setFieldsValue(untouchedFields);
    if (!form.isFieldTouched('shippingAddress')) {
      setShippingState({
        shipAdd1: isUndefinedNullOrEmpty(shippingAdd1)
          ? normalizedAddress.address1
          : shippingAdd1,
        shipAdd2: isUndefinedNullOrEmpty(shippingAdd2)
          ? normalizedAddress.address2
          : shippingAdd2,
        shipCity: isUndefinedNullOrEmpty(shippingCity)
          ? normalizedAddress.city
          : shippingCity,
        shipState: isUndefinedNullOrEmpty(shippingState)
          ? normalizedAddress.state
          : shippingState,
        shipZip: isUndefinedNullOrEmpty(shippingZip)
          ? normalizedAddress.zipCode
          : shippingZip,
        shipAddress: isUndefinedNullOrEmpty(shippingAddress)
          ? getAggregatedAddress(normalizedAddress)
          : shippingAddress,
      });
    }
  }, [
    orderInfo.firstName,
    orderInfo.lastName,
    orderInfo.email,
    orderInfo.phoneNum,
    orderInfo.installPhone,
    businessInfo.businessName,
    businessInfo.businessPhone,
    businessInfo.noOfEmployees,
    businessInfo.noOfLocations,
  ]);

  // Optional prop, currently only passed by the single-page checkout's "Continue with
  // Google" flow (YourInformationSection.js) - this form is already mounted (SSO buttons and
  // fields render together there) by the time sign-in resolves, so the mount-only effect
  // above can't pick it up; this fires whenever a new prefill payload arrives instead. Does
  // not touch orderInfo/redux, so it can't affect any other caller of this component.
  useEffect(() => {
    if (props.prefillValues) {
      form.setFieldsValue(props.prefillValues);
      // setFieldsValue alone doesn't re-run validation - if a field already had a validation
      // error showing (e.g. the customer blurred an empty First Name before using Google
      // Sign-In), it would otherwise linger even though the field now holds a valid
      // Google-provided value. Same class of bug as the browser-autofill fix above, so it
      // gets the same treatment: explicitly re-validate the fields Google just populated.
      form.validateFields(Object.keys(props.prefillValues)).catch(() => {});
      // The customer never blurs the email field on this path, so run the same Kickbox +
      // account-exists check a blur would - otherwise an unverified email, or one that
      // already has an account, goes straight through on submit.
      if (props.prefillValues.email) {
        verifyEmail(props.prefillValues.email);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.prefillValues]);

  // Browser autofill sets the input's DOM value directly rather than through a normal
  // input/change event, so React (and antd's Form on top of it) never learns the value
  // changed - the field renders the autofilled text but the form still treats it as empty,
  // e.g. a filled-in Last Name still shows "Please enter Last Name" until the user types into
  // it themselves. Autofilled inputs DO reliably fire a CSS animation on the browser's
  // :-webkit-autofill pseudo-class (see the onAutoFillStart keyframes in _checkout.scss),
  // which gives us a real DOM event to hook into and re-sync the actual value into the form -
  // the same setFieldsValue/validateFields calls a manual edit would already trigger, so this
  // doesn't add any parallel validation state.
  // Scoped to showFieldLabels (only ever true for the single-page checkout, see
  // YourInformationSection.js) so this fix - and its behavior - stays isolated to that flow;
  // the multi-step checkout's copy of this form is untouched.
  const handleAutoFill = (fieldName) => (e) => {
    if (!props.showFieldLabels || e.animationName !== 'onAutoFillStart') {return;}
    const { value } = e.target;
    if (isUndefinedNullOrEmpty(value) || form.getFieldValue(fieldName) === value) {return;}
    form.setFieldsValue({ [fieldName]: value });
    form.validateFields([fieldName]).catch(() => {});
  };

  // The phone field stores/validates a normalized "+1XXXXXXXXXX" value (see the mount effect
  // above and handleOnBlurPhone) while the browser autofills the field's own formatted text
  // (e.g. "(404) 555-0148") - reusing handleAutoFill's raw value here would desync the two, so
  // this normalizes to the same shape before handing off to the same sync/validate path.
  const handlePhoneAutoFill = (e) => {
    if (!props.showFieldLabels || e.animationName !== 'onAutoFillStart') {return;}
    const digits = e.target.value.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) {return;}
    handleAutoFill('phoneNum')({ animationName: e.animationName, target: { value: `+1${digits}` } });
  };

  const handleSubmit = (values) => {
    if (emailVerificationLoading || phoneVerificationLoading) {
      pendingSubmitRef.current = true;
      return;
    }
    pendingSubmitRef.current = false;

    const callLogId = getCallLogId();
    const { firstName, lastName, email, phoneNum, installPhone } = values;
    const { serviceCheckId } = serviceabilityCheck;

    if (!emailVerificationError && !phoneVerificationError && isEmailAvail) {
      if (isBusinessAddress) {
        const { businessName, businessPhone, noOfEmployees, noOfLocations } =
          values;
        dispatch(
          saveBusinessInfo({
            businessName,
            businessPhone,
            noOfEmployees,
            noOfLocations,
          })
        );
      }

      dispatch(saveSpanishLanguagePreference(spanishLanguagePref));

      dispatch(savePhoneNumber(phoneNum.slice(-10)))
      if (!isUndefinedNullOrEmpty(orderAccessToken)) {
        const personalInfoModel = getEnterInfoModel(
          firstName,
          lastName,
          email,
          phoneNum.slice(-10),
          callLogId,
          serviceCheckId
        );
        const installPhoneValue =
          !isUndefinedNullOrEmpty(installPhone) && installPhone?.length === 12
            ? { installPhone: installPhone.slice(2) }
            : {};

        const finalShipAddress = getAggregatedAddress({
          address1: shipAdd1,
          address2: shipAdd2,
          city: shipCity,
          state: shipState,
          zipCode: shipZip,
        });
        const finalServiceAddress = getAggregatedAddress({
          address1,
          address2,
          city,
          state,
          zipCode,
        });

        const ReqBody = {
          ...personalInfoModel,
          ...installPhoneValue,
          ...(internetPlan?.servCode === 'ELFW' && {
            shippingAdd1: isAddressDifferent ? shipAdd1 : address1,
            shippingAdd2: isAddressDifferent ? shipAdd2 : address2,
            shippingAddress: isAddressDifferent
              ? finalShipAddress
              : finalServiceAddress,
            shippingCity: isAddressDifferent ? shipCity : city,
            shippingState: isAddressDifferent ? shipState : state,
            shippingZip: isAddressDifferent ? shipZip : zipCode,
          }),
        };
        // SAVE TO DB CALL
        dispatch(updateOrder(ReqBody, orderAccessToken))
          .then(() => {
            props.setActiveKey('4');
            leads?.updateLead?.('lead survey', {
              firstName: firstName,
              lastName: lastName,
              emails: [{ email: email }],
              phones: [
                phoneNum?.trim() ? { number: phoneNum } : undefined,
                isBusinessAddress && values?.businessPhone?.trim()
                  ? { number: values?.businessPhone, type: 'business' }
                  : undefined,
              ].filter(Boolean),
              data: {
                businessName: isBusinessAddress
                  ? values?.businessName
                  : undefined,
                employees: isBusinessAddress
                  ? values?.noOfEmployees
                  : undefined,
                languagePref: spanishLanguagePref ? 'spanish' : 'english',
              },
            });
          })
          .catch((error) => {
            dispatch(updateErrorMsg(error));
          });
      }
    }
  };

  useEffect(() => {
    if (pendingSubmitRef.current && !emailVerificationLoading && !phoneVerificationLoading) {
      pendingSubmitRef.current = false;
      form
        .validateFields()
        .then(() => handleSubmit(form.getFieldsValue(true)))
        .catch(() => {});
    }
  }, [emailVerificationLoading, phoneVerificationLoading]);

  const handleKeyPress = (value) => {
    const phoneEl = document.getElementById(value);
    const phoneNumber = phoneEl && phoneEl?.value;
    if (!isUndefinedNullOrEmpty(phoneNumber)) {
      if (
        phoneNumber.length === 1 &&
        (phoneNumber === '0' || phoneNumber === '1')
      ) {
        phoneEl.value = '';
      }
      if (phoneNumber.charAt(0) === '1' || phoneNumber.charAt(0) === '0') {
        phoneEl.value = '';
      }
    }
    return;
  };

  const handleKeyDown = (value) => {
    const num = document.getElementById(value);
    const number = num && num?.value;
    if (!isUndefinedNullOrEmpty(number)) {
      if (number.length === 1 && number === '0') {
        num.value = '';
      }
    }
    return;
  };

  // emailOverride: the Google prefill passes the email it just set, since the input's DOM
  // value hasn't re-rendered yet. As an onBlur handler this receives the event instead.
  const verifyEmail = (emailOverride) => {
    setEmailVerificationError('');
    setEmailVerificationLoading(true);
    const email = (typeof emailOverride === 'string'
      ? emailOverride
      : (emailInp?.current?.input?.value || form.getFieldValue('email'))) ?? '';
    if (email && email !== emailVal) {

      if (!email || email?.length === 0) {
        setEmailVerificationError(t('validation.enterEmail'));
        setEmailVerificationLoading(false);
      } else {
        form.setFieldsValue({ email: email?.trim() });
        emailNotVerifiedRef.current = false;
        api
          .axios({
            method: 'GET',
            url: `${config.API_ENDPOINT}/emails/verify?email=${encodeURIComponent(email)}&appClientId=www`,
          })
          .then((res) => {
            const { result } = res.data;
            if (result === 'undeliverable') {
              emailNotVerifiedRef.current = true;
              setEmailVerificationError(
                t('validation.undeliverableEmail')
              );
              setEmailVerificationLoading(false);
            } else {
              checkAcctAvail();
            }
          })
          .catch(() => {
            // A failed request says nothing about the email, so fail open rather than block
            // every checkout during an outage - but still run the account-exists check.
            checkAcctAvail();
          });
      }
    } else {
      if (isUndefinedNullOrEmpty(email)) {
        setEmailVerificationError(t('validation.pleaseEnterEmail'));
      } else {
        props.setShowCallSupportModal(true);
        pendingSubmitRef.current = false;
        setEmailVerificationError('');
      }
      setEmailVerificationLoading(false);
    }
  };

  const checkEmail = () => {
    const email = (emailInp?.current?.input?.value || form.getFieldValue('email')) ?? '';

    if (!email || email?.length === 0) {
      setEmailVerificationError(t('validation.enterEmail'));
    }
  };

  const checkAcctAvail = () => {
    setEmailVerificationLoading(true);

    const email = (emailInp?.current?.input?.value || form.getFieldValue('email')) ?? '';
    const emailValid = validator.isEmail(email) && emailRegex.test(email);

    if (email && !emailValid) {
      setEmailVerificationError(t('validation.invalidEmail'));
      setEmailVerificationLoading(false);
      return;
    }

    setEmailVerificationError('');

    if (email && !isElnkMailboxReq) {
      const accEmail = getValidAcctEmail(email, isElnkMailboxReq);
      const { firstName, lastName } = form.getFieldsValue([
        'firstName',
        'lastName',
      ]);
      if (
        !isUndefinedNullOrEmpty(accEmail) &&
        !isUndefinedNullOrEmpty(firstName) &&
        !isUndefinedNullOrEmpty(lastName)
      ) {
        const accountCheckModel = getAccountCheckModel(
          firstName,
          lastName,
          accEmail
        );
        dispatch(accountCheck(accountCheckModel))
          .then((res) => {
            const accountData = res?.value?.data;
            const isAvail = accountData?.suggestedIsValid || false;
            if (isAvail) {
              setEmailVal('');
              setIsEmailAvail(true);
              setEmailVerificationLoading(false);
              props.setShowCallSupportModal(false);
            } else {
              setEmailVal(email);
              setIsEmailAvail(false);
              pendingSubmitRef.current = false;
              setEmailVerificationLoading(false);
              props.setShowCallSupportModal(true);
            }
          })
          .catch((err) => {
            const errorCode = err?.response?.data?.errorCode;
            setIsEmailAvail(false);
            pendingSubmitRef.current = false;
            setEmailVerificationLoading(false);

            // midas errorCode: 2053, account already in use
            if (errorCode && errorCode === 2053) {
              setEmailVal(email);
              props.setShowCallSupportModal(true);
            } else {
              // Any other failure isn't a verdict on the email - don't cache it, so the next
              // blur checks it again instead of treating it as taken.
              setEmailVal('');
              setEmailVerificationError(t('validation.tryAgain'));
            }
          });
      } else {
        setIsEmailAvail(true);
        setEmailVerificationLoading(false);
      }
    } else {
      setIsEmailAvail(true);
      setEmailVerificationLoading(false);
    }
  };

  const handleOnBlurName = async () => {
    if (
      !props.showCreateAccTile &&
      !isElnkMailboxReq &&
      !emailNotVerifiedRef.current &&
      form.getFieldValue('firstName') &&
      form.getFieldValue('lastName') &&
      form.getFieldValue('email')
    ) {
      try {
        checkAcctAvail();
      } catch { }
    }
  };

  useImperativeHandle(ref, () => ({
    emailEdit() {
      handleEditEmail();
    },
  }));

  const handleEditEmail = () => {
    props.setShowCallSupportModal(false);

    form.setFieldsValue({
      email: '',
    });
    setTimeout(() => {
      emailInp.current.input.focus();
    }, 300);
    return;
  };

  const handleManualChange = (e, name) => {
    setManualAddress({
      ...manualAddress,
      [name]: name === 'state' ? e : e.target.value,
    });
  };

  const verifyMobileApi = (phoneVal) => {
    return api.axios({
      method: 'GET',
      url: `${config.API_ENDPOINT}/phone/check/${phoneVal?.slice(-10)}`,
    })
  }

  const handleOnBlurPhone = useCallback(() => {
    setPhoneVerificationError('');
    setPhoneVerificationWarning('');
    let phoneVal = form.getFieldValue('phoneNum')
    if (checkTollFreeNumber(phoneVal?.toString())) {
      setPhoneVerificationError(t('validation.validMobilePhone'));
    } else if (phoneVal?.length > 11) {
      setPhoneVerificationLoading(true);
      verifyMobileApi(phoneVal)
        .then((res) => {
          setPhoneVerificationLoading(false);
          const carrierType = res?.data?.response?.carrier?.type;
          if (carrierType === 'mobile') {
            setPhoneVerificationWarning('');
          } else {
            setPhoneVerificationWarning(t('enterInfo.mobilePhoneWarning'));
          }
          if (res?.data?.response?.carrier?.name) {
            dispatch(saveMobileProvider(res?.data?.response?.carrier?.name || ''))
          }
        })
        .catch(() => {
          setPhoneVerificationLoading(false);
          setPhoneVerificationWarning(t('enterInfo.mobilePhoneWarning'));
        });
    }
  }, [t]);

  const handleOnBlurInstallPhone = useCallback(() => {
    let phoneVal = form.getFieldValue('installPhone');
    if (phoneVal?.length > 11) {
      verifyMobileApi(phoneVal)
    }
  }, [])

  const isVaildManualAddress =
    manualAddress.address1 !== '' &&
    manualAddress.city !== '' &&
    manualAddress.zip !== '' &&
    manualAddress.zip.length > 4 &&
    manualAddress.state !== '';
  const buttonCheckAvailability =
    isAddressDifferent && isManualAddr && !isVaildManualAddress ? true : false;

  return (
    <>
      <div className="your-info-tab same-common">
        <Form
          name="basic"
          initialValues={initialState}
          onFinish={handleSubmit}
          onValuesChange={() => {
            pendingSubmitRef.current = false;
          }}
          autoComplete="off"
          form={form}
          validateTrigger={'onBlur'}
          className="form-info-tab"
        >
          <Row className="yor-info-row j-between">
            {isBusinessAddress && (
              <>
                <Col span={24} className="p-left-15-p responsive-width-set">
                  <h2>{t('enterInfo.businessInfo')}</h2>
                </Col>
                <Col span={12} className="your-info-input responsive-width-set">
                  <Form.Item
                    label={t('enterInfo.businessName')}
                    name="businessName"
                    validateFirst={'true'}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: t('enterInfo.enterBusinessName'),
                      },
                      {
                        min: 2,
                        message: t('validation.min2Length'),
                      },
                      {
                        pattern: notOnlyNumerics,
                        message: t('validation.notOnlyNumerics'),
                      },
                    ]}
                  >
                    <Input maxLength="30" />
                  </Form.Item>
                </Col>
                <Col span={12} className="your-info-input responsive-width-set">
                  <Form.Item
                    label={t('enterInfo.businessPhone')}
                    name="businessPhone"
                    rules={[
                      { required: true, message: t('enterInfo.enterPhoneNumber') },
                      {
                        min: 10,
                        message: t('validation.min10Length'),
                      },
                      {
                        validator(_, value) {
                          if (!value) {
                            return Promise.resolve();
                          } else if (!isValidPhoneNumber(value)) {
                            return Promise.reject(
                              t('validation.invalidPhoneNumber')
                            );
                          } else if (checkTollFreeNumber(value.toString())) {
                            return Promise.reject(
                              t('validation.tollFreeNotAllowed')
                            );
                          } else {
                            return Promise.resolve();
                          }
                        },
                      },
                    ]}
                  >
                    <PhoneInput
                      smartCaret={false}
                      useNationalFormatForDefaultCountryValue={true}
                      defaultCountry="US"
                      autoComplete="off"
                      maxLength={14}
                      name="businessPhone"
                      id="businessPhone"
                      disableCountryCode={true}
                      onKeyPress={() => handleKeyPress('businessPhone')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} className="your-info-input responsive-width-set">
                  <Form.Item
                    label={t('enterInfo.noOfLocations')}
                    name="noOfLocations"
                    rules={[
                      {
                        required: true,
                        message: t('enterInfo.enterLocations'),
                      },
                      {
                        validator(_, value) {
                          if (value && (value === '0' || value === 0)) {
                            return Promise.reject(
                              new Error(t('validation.businessRequired'))
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      maxLength="4"
                      onKeyPress={handleNumericKeyPress}
                      id="noOfLocations"
                      onKeyDown={() => {
                        handleKeyDown('noOfLocations');
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} className="your-info-input responsive-width-set">
                  <Form.Item
                    label={t('enterInfo.noOfEmployees')}
                    name="noOfEmployees"
                    rules={[
                      {
                        required: true,
                        message: t('enterInfo.enterEmployees'),
                      },
                      {
                        validator(_, value) {
                          if (value && (value === '0' || value === 0)) {
                            return Promise.reject(
                              new Error(t('validation.businessRequired'))
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      maxLength="4"
                      min="0"
                      onKeyPress={handleNumericKeyPress}
                      id="noOfEmployees"
                      onKeyDown={() => {
                        handleKeyDown('noOfEmployees');
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24} className="p-left-15-p">
                  <h2>{t('enterInfo.nameAndContact')}</h2>
                </Col>
              </>
            )}
            <Col span={12} className="your-info-input responsive-width-set">
              <Form.Item
                name="firstName"
                label={props.showFieldLabels ? t('singlePageCheckout.firstNameLabel') : undefined}
                validateFirst={'true'}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('enterInfo.enterFirstName'),
                  },
                  {
                    min: 2,
                    message: t('validation.min2Length'),
                  },
                  {
                    validator(_, value) {
                      if (!isUndefinedNullOrEmpty(value)) {
                        value = value.trim();
                        form.setFieldsValue({ firstName: value });
                        if (!alphaWithSpaceRegex.test(value)) {
                          form.setFieldsValue({ firstName: '' });
                          return;
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder={t('enterInfo.firstNamePlaceholder')}
                  maxLength="30"
                  onBlur={handleOnBlurName}
                  onKeyPress={handleAlphaKeyPress}
                  onAnimationStart={handleAutoFill('firstName')}
                  onChange={() => {
                    setTimeout(() => {
                      if (form.getFieldValue('lastName')) {
                        form.validateFields(['lastName']).catch(() => {});
                      }
                    }, 0);
                  }}
                  className="input-box"
                />
              </Form.Item>
            </Col>
            <Col span={12} className="your-info-input responsive-width-set">
              <Form.Item
                name="lastName"
                label={props.showFieldLabels ? t('singlePageCheckout.lastNameLabel') : undefined}
                validateFirst={'true'}
                dependencies={['firstName']}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('enterInfo.enterLastName'),
                  },
                  {
                    min: 2,
                    message: t('validation.min2Length'),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!isUndefinedNullOrEmpty(value)) {
                        value = value.trim();
                        form.setFieldsValue({ lastName: value });
                        if (!alphaWithSpaceRegex.test(value)) {
                          form.setFieldsValue({ lastName: '' });
                          return;
                        }
                        if (
                          value &&
                          getFieldValue('firstName')?.toLowerCase() ===
                          value.toLowerCase()
                        ) {
                          return Promise.reject(
                            new Error(t('validation.valuesUnique'))
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  placeholder={t('enterInfo.lastNamePlaceholder')}
                  maxLength="30"
                  onBlur={handleOnBlurName}
                  onKeyPress={handleAlphaKeyPress}
                  onAnimationStart={handleAutoFill('lastName')}
                  className="input-box"
                />
              </Form.Item>
            </Col>
            <Col span={props.showFieldLabels ? 12 : 24} className="your-info-input responsive-width-set">
              {props.showFieldLabels && <label>{t('singlePageCheckout.emailLabel')}</label>}
              {/* The spinner is positioned relative to this div, not the Form.Item, so the
                  label above (rendered separately here so it isn't part of the same relative
                  box) doesn't push its vertical center down past the input itself. */}
              <div style={{ position: 'relative' }}>
                <Form.Item
                  name="email"
                  validateStatus={emailVerificationError ? 'error' : undefined}
                  help={emailVerificationError || undefined}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t('validation.enterEmail'),
                    },
                    {
                      type: 'email',
                      message: t('validation.invalidEmail'),
                    },
                  ]}
                >
                  <Input
                    placeholder={t('enterInfo.emailPlaceholder')}
                    ref={emailInp}
                    onBlur={verifyEmail}
                    onAnimationStart={handleAutoFill('email')}
                    onChange={() => {
                      if (emailVerificationError) {setEmailVerificationError('');}
                    }}
                    className="input-box"
                  />
                </Form.Item>
                {emailVerificationLoading && (
                  <img
                    src={spinner}
                    alt=""
                    style={{
                      width: '15px',
                      height: '15px',
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            </Col>
            <Col span={props.showFieldLabels ? 12 : 24} className="your-info-input responsive-width-set">
              {props.showFieldLabels && <label>{t('singlePageCheckout.mobileLabel')}</label>}
              <div style={{ position: 'relative', top: props.showFieldLabels ? '0px' : '2px' }}>
                <Form.Item
                  className="custom-form-input"
                  for="phoneNum"
                  name="phoneNum"
                  rules={[
                    { required: true, message: t('enterInfo.enterPhoneNumber') },
                    {
                      min: 12,
                      message: t('validation.min10Length'),
                    },
                  ]}
                >
                  <PhoneInput
                    smartCaret={false}
                    useNationalFormatForDefaultCountryValue={true}
                    defaultCountry="US"
                    autoComplete="off"
                    maxLength={14}
                    name="phoneNum"
                    id="phoneNum"
                    className="input-box ant-input"
                    disableCountryCode={true}
                    onKeyPress={() => handleKeyPress('phoneNum')}
                    onBlur={handleOnBlurPhone}
                    onAnimationStart={handlePhoneAutoFill}
                    placeholder={props.showFieldLabels ? t('singlePageCheckout.mobileNumberPlaceholder') : t('enterInfo.mobilePlaceholder')}
                  />
                </Form.Item>
                {phoneVerificationLoading && (
                  <img
                    src={spinner}
                    alt=""
                    style={{ width: '15px', height: '15px', position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                  />
                )}
              </div>
              {!!phoneVerificationError && (
                <span style={{ color: '#ff4d4f' }}>{phoneVerificationError}</span>
              )}
            </Col>
            {!phoneVerificationError && !!phoneVerificationWarning && (
              <span className="mobile-warning-msg">{phoneVerificationWarning}</span>
            )}
            {internetPlan?.servCode === 'ELFW' && (
              <>
                <Col
                  span={20}
                  className="your-info-input responsive-width-set ship-addr-col"
                >
                  <label htmlFor="shipAddress" className="custom-label">
                    {t('enterInfo.addShippingAddress')}{' '}
                    <span style={{ color: '#ff4d4f' }}>
                      {t('enterInfo.differentFromService')}
                    </span>
                  </label>
                </Col>
                <Col span={4} className="your-info-input responsive-width-set">
                  <div className="shipping_icon text-right">
                    <button type="button" onClick={() => setIsAddressDifferent((prev) => !prev)}>
                      <i
                        className={`fas fa-${isAddressDifferent ? 'minus' : 'plus'}-circle`}
                      ></i>
                    </button>
                  </div>
                </Col>
                {isAddressDifferent && (
                  <>
                    <Col
                      span={18}
                      className="your-info-input responsive-width-set"
                    >
                      <Form.Item
                        className="address-on-card"
                        name="shippingAddress"
                        initialValue={getAggregatedAddress({
                          address1: shippingAdd1,
                          address2: shippingAdd2,
                          city: shippingCity,
                          state: shippingState,
                          zipCode: shippingZip,
                        })}
                      >
                        {
                          <AddressSuggestionSelect
                            autoFocus={false}
                            autoComplete="off"
                            parentComp="shipping"
                            className="shippingInput"
                            setShipping={setShipping}
                            val={shippingAddrState.shipAddress}
                            name="shippingAddress"
                            disabled={isManualAddr}
                          />
                        }
                      </Form.Item>
                    </Col>
                    <Col
                      span={6}
                      className="your-info-input marin-top-8 responsive-width-set"
                    >
                      <Checkbox
                        checked={isManualAddr}
                        onChange={() => setIsManualAddr(!isManualAddr)}
                      >
                        {t('enterInfo.enterManually')}
                      </Checkbox>
                    </Col>
                    {isManualAddr && (
                      <>
                        <Col
                          span={12}
                          className="your-info-input responsive-width-set"
                        >
                          <Form.Item label={t('enterInfo.streetAddress')} name="address1">
                            <Input
                              value={manualAddress?.address1}
                              onChange={(e) =>
                                handleManualChange(e, 'address1')
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          span={12}
                          className="your-info-input responsive-width-set"
                        >
                          <Form.Item label={t('enterInfo.aptFlrUnit')} name="address2">
                            <Input
                              value={manualAddress?.address2}
                              onChange={(e) =>
                                handleManualChange(e, 'address2')
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          span={8}
                          className="your-info-input responsive-width-set"
                        >
                          <Form.Item label={t('enterInfo.city')} name="city">
                            <Input
                              value={manualAddress?.city}
                              onChange={(e) => handleManualChange(e, 'city')}
                            />
                          </Form.Item>
                        </Col>
                        <Col
                          span={8}
                          className="your-info-input responsive-width-set"
                        >
                          <Form.Item
                            label={t('enterInfo.state')}
                            name="state"
                            style={{ fontSize: '16px' }}
                          >
                            <Select
                              id="stateInput"
                              value={manualAddress?.state}
                              onChange={(e) => handleManualChange(e, 'state')}
                            >
                              {Object.keys(STATES).map((abbreviation) => (
                                <Select.Option
                                  key={abbreviation}
                                  value={abbreviation}
                                >
                                  {abbreviation}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col
                          span={8}
                          className="your-info-input responsive-width-set"
                        >
                          <Form.Item label={t('enterInfo.zipCode')} name="zipCode">
                            <Input
                              maxLength="5"
                              value={manualAddress?.zip}
                              onKeyPress={handleNumericKeyPress}
                              onChange={(e) => handleManualChange(e, 'zip')}
                            />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Row>

          {!isVasOnlyFlow && !props.showFieldLabels &&
            <>
              <div className="primary-info" style={{padding : '26px' , marginTop : '10px' }} >
                <i className="fa-solid fa-circle-info" style={{ color: '#57585C', fontSize: '16px',alignContent: 'center', marginLeft : '-5px' }}></i>&nbsp;
                <p>
                  <b>{t('enterInfo.mobilePhoneInfoBold')} </b>{t('enterInfo.mobilePhoneInfo')}
                </p>
              </div>
              {/* -----Intallation Number---- */}
              <Col span={24}>
                <Collapse
                  className="installNum"
                  activeKey={open}
                  onChange={() => setOpen(() => [1])}
                >
                  <Panel key="1">
                    <Row>
                      <Col span={8}>
                        <Form.Item
                          name="installPhone"
                          dependencies={['phoneNum']}
                          rules={[
                            {
                              min: 12,
                              message: t('validation.min10Length'),
                            },
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (!value) {
                                  return Promise.resolve();
                                } else if (
                                  value &&
                                  getFieldValue('phoneNum') &&
                                  getFieldValue('phoneNum') === value
                                ) {
                                  return Promise.reject(
                                    new Error(t('validation.differentFromPrimary'))
                                  );
                                } else {
                                  return Promise.resolve();
                                }
                              },
                            }),
                          ]}
                        >
                          <PhoneInput
                            placeholder={t('enterInfo.activationPlaceholder')}
                            smartCaret={false}
                            useNationalFormatForDefaultCountryValue={true}
                            defaultCountry="US"
                            autoComplete="off"
                            maxLength={14}
                            name="InstallPhoneNum"
                            id="InstallPhoneNum"
                            className="ant-input"
                            disableCountryCode={true}
                            onKeyPress={() => handleKeyPress('InstallPhoneNum')}
                            onBlur={handleOnBlurInstallPhone}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Panel>
                </Collapse>
              </Col>
            </>
          }
          <Form.Item shouldUpdate noStyle>
            {() => {
              // Verification just grays the button out (is-verifying) rather than disabling it.
              // The submit click itself often blurs the email field and starts verification -
              // a real `disabled` would swallow that click, so it stays clickable and
              // handleSubmit queues it via pendingSubmitRef. "Processing..." is only for the save.
              const showVerifyingState = (emailVerificationLoading || phoneVerificationLoading) && !loading;
              let label = props.showFieldLabels ? t('singlePageCheckout.saveInformation') : t('common.next');
              if (loading) {
                label = t('common.processing');
              }
              return (
                <div className="text-right enter-your-info">
                  <button
                    className={`blue-button${showVerifyingState ? ' is-verifying' : ''}`}
                    htmlType="submit"
                    onClick={checkEmail}
                    aria-disabled={showVerifyingState || undefined}
                    disabled={
                      loading || buttonCheckAvailability || !!emailVerificationError || !!phoneVerificationError
                      || (props.showFieldLabels && isFormIncomplete(form, ['firstName', 'lastName', 'email', 'phoneNum']))
                    }
                  >
                    {label}
                  </button>
                </div>
              );
            }}
          </Form.Item>
        </Form>
      </div>
    </>
  );
});

EnterInformation.propTypes = {
  setShowCallSupportModal: PropTypes.bool.isRequired,
  prefillValues: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
  }),
  showFieldLabels: PropTypes.bool,
};

export default EnterInformation;
